/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 * @author Dominik Böck <d.boeck@techdivision.com>
 */

import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useBestFitQuery } from '../../../Hooks/Api/useBestFitQuery';

/**
 * Custom useAppointmentForm hook
 * @returns {{isLoading, handleBack: handleBack, slots: *[], data, slotsToRender: *[], activeStep: number, handleNext: handleNext}}
 */
export function useAppointmentForm() {
  const { t } = useTranslation();
  const {
    values,
    setFieldValue,
  } = useFormikContext();
  const [slots, setSlots] = useState([]);
  const [activeStep, setActiveStep] = useState(values.activeStep || 0);
  const [slotsToRender, setSlotsToRender] = useState([]);
  const [slotsInitialized, setSlotsInitialized] = useState(false);
  const clientsTimeZone = dayjs.tz.guess();

  const {
    data,
    isLoading,
  } = useBestFitQuery('trialSessionSlots', {
    studioId: values.studioId,
    startDate: dayjs()
      .tz(clientsTimeZone)
      .format('YYYY-MM-DD'),
    endDate: dayjs()
      .tz(clientsTimeZone)
      .add(14, 'day')
      .format('YYYY-MM-DD'),
  });

  /**
     * Build one Timeslot Entry
     *
     * @param startDateTimeLabel
     * @param endDateTimeLabel
     * @param dateSlot
     * @returns {{label: string, value: string}}
     */
  const buildTimeSlot = useCallback((dateSlot, startDateTimeLabel, endDateTimeLabel) => ({
    label: t('fromToTime', {
      startTime: startDateTimeLabel,
      endTime: endDateTimeLabel,
    }),
    value: `${dateSlot.startDateTime}+${dateSlot.endDateTime}`,
  }), [t]);

  /**
     * Get the restriction mapping for allowed timeslots
     *
     * @param withTrainer
     * @returns {{duringWeek: {latestEnd: string, earliestStart: string}, weekend: {latestEnd: string, earliestStart: string}}}
     */
  const getTimeSlotRestrictionMapping = useCallback((withTrainer) => {
    // Default Date restrictions
    let dateRestrictions = {
      weekend: {
        earliestStart: '07:00',
        latestEnd: '22:00',
      },
      duringWeek: {
        earliestStart: '06:00',
        latestEnd: '24:00',
      },
    };
    // Change the date restrictions, if the appointment should be with trainer
    if (withTrainer === 'true') {
      dateRestrictions = {
        weekend: {
          earliestStart: '09:00',
          latestEnd: '17:00',
        },
        duringWeek: {
          earliestStart: '09:00',
          latestEnd: '21:00',
        },
      };
    }
    return dateRestrictions;
  }, []);

  /**
     * Checks if the timeslot is in the opening time of the studio
     *
     * @param dateSlot
     * @param formOfTraining
     * @param startDateTime
     * @param endDateTime
     * @returns {boolean}
     */
  const isTimeSlotInOpeningTime = useCallback((dateSlot, formOfTraining, startDateTime, endDateTime) => {
    const timeSlotRestrictionsMapping = getTimeSlotRestrictionMapping(formOfTraining);
    let timeSlotRestrictions = timeSlotRestrictionsMapping.duringWeek;
    const dayOfWeek = dayjs(dateSlot.startDateTime)
      .tz(clientsTimeZone)
      .day();
    const isWeekend = dayOfWeek === 6 || dayOfWeek === 0;
    if (isWeekend) {
      timeSlotRestrictions = timeSlotRestrictionsMapping.weekend;
    }
    return startDateTime >= timeSlotRestrictions.earliestStart && endDateTime <= timeSlotRestrictions.latestEnd;
  }, [clientsTimeZone, getTimeSlotRestrictionMapping]);

  /**
     * Transform slot data
     * @returns {*[]}
     */
  const transformSlotData = useCallback((dataSlots) => {
    const transformedData = [];
    dataSlots.forEach((slot) => {
      const dateWithoutTime = dayjs(slot.startDateTime)
        .locale('de')
        .tz(clientsTimeZone)
        .format('DD.MM.YY dddd');
      const startDateTime = dayjs(slot.startDateTime)
        .tz(clientsTimeZone)
        .format('HH:mm');
      const endDateTime = dayjs(slot.endDateTime)
        .tz(clientsTimeZone)
        .format('HH:mm');
      // Fallback empty array
      if (!transformedData[dateWithoutTime]) {
        transformedData[dateWithoutTime] = [];
      }
      // Only add the timeslot, if it's in the opening time
      if (isTimeSlotInOpeningTime(slot, values.formOfTraining, startDateTime, endDateTime)) {
        transformedData[dateWithoutTime].push(buildTimeSlot(slot, startDateTime, endDateTime));
      }
    });
    return transformedData;
  }, [buildTimeSlot, clientsTimeZone, isTimeSlotInOpeningTime, values.formOfTraining]);

  /**
     * Initialize slots data
     */
  useEffect(() => {
    if (data) {
      const transformedSlotData = transformSlotData(data.slots);
      setSlots(transformedSlotData);
      setSlotsToRender(transformedSlotData[Object.keys(transformedSlotData)[activeStep]]);
      setSlotsInitialized(true);
    }
  }, [activeStep, data, transformSlotData]);

  /**
     * Set new slots when date changed
     */
  useEffect(() => {
    setFieldValue('activeStep', activeStep);
    if (slotsInitialized) {
      setSlotsToRender(slots[Object.keys(slots)[activeStep]]);
    }
  }, [activeStep, setFieldValue, slots, slotsInitialized]);

  /**
     * Handle next step
     */
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  /**
     * Handle back step
     */
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return {
    data,
    isLoading,
    activeStep,
    slots,
    slotsToRender,
    handleBack,
    handleNext,
  };
}
